import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { 
  Zap, 
  TrendingUp, 
  ShieldCheck, 
  MessageCircle, 
  Camera, 
  Star, 
  Check, 
  Upload,
  Menu,
  X,
  Instagram,
  ArrowRight,
  DollarSign,
  Users,
  Clock,
  Sun,
  Moon,
  MapPin,
  Plane,
  Heart,
  Globe,
  Percent,
  Wallet,
  Lock,
  Unlock,
  Gift,
  Coffee,
  Sparkles,
  UserPlus,
  ChevronDown,
  ExternalLink,
  Coins
} from "lucide-react";
import { Toaster, toast } from "sonner";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ViewApplicationPage from "./pages/ViewApplicationPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import UserManagementPage from "./pages/UserManagementPage";
// Public User Pages
import { PublicAuthProvider } from "./context/PublicAuthContext";
import RegisterPage from "./pages/public/RegisterPage";
import LoginPage from "./pages/public/LoginPage";
import ForgotPasswordPage from "./pages/public/ForgotPasswordPage";
import ResetPasswordPage from "./pages/public/ResetPasswordPage";
import VerifyEmailPage from "./pages/public/VerifyEmailPage";
import ProfilePage from "./pages/public/ProfilePage";
import PaymentInfoPage from "./pages/public/PaymentInfoPage";
import CreatorLandingPage from "./pages/public/CreatorLandingPage";
import PublicCreatorProfilePage from "./pages/public/PublicCreatorProfilePage";
import AdminCreatorProfilePage from "./pages/AdminCreatorProfilePage";
import AdminFanProfilePage from "./pages/AdminFanProfilePage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminCreatorRequestsPage from "./pages/AdminCreatorRequestsPage";
import AdminPackagesPage from "./pages/AdminPackagesPage";
import AdminTransactionsPage from "./pages/AdminTransactionsPage";
import ImpersonationBanner from "./components/ImpersonationBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Hook
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hotpeek-theme') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('hotpeek-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return { theme, toggleTheme };
};

// Top Announcement Banner Component
const TopBanner = ({ onHeightChange }) => {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${API}/public/site-settings/banner`);
        if (response.data.enabled) {
          setBanner(response.data);
        }
      } catch (err) {
        // Silently fail - banner is optional
      }
    };
    fetchBanner();
  }, []);

  // Report banner height to parent
  useEffect(() => {
    if (onHeightChange) {
      if (banner && !dismissed && bannerRef.current) {
        onHeightChange(bannerRef.current.offsetHeight);
      } else {
        onHeightChange(0);
      }
    }
  }, [banner, dismissed, onHeightChange]);

  if (!banner || dismissed) return null;

  const handleClick = () => {
    if (banner.button_url) {
      if (banner.button_url.startsWith('http')) {
        window.open(banner.button_url, '_blank');
      } else {
        window.location.href = banner.button_url;
      }
    }
  };

  return (
    <div 
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white z-[60]" 
      data-testid="top-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-4 text-sm">
        <span className="text-center">{banner.message}</span>
        {banner.button_text && banner.button_url && (
          <button
            onClick={handleClick}
            className="px-4 py-1 bg-white text-purple-600 rounded-full font-semibold text-xs hover:bg-gray-100 transition-colors flex items-center gap-1 whitespace-nowrap"
            data-testid="top-banner-cta"
          >
            {banner.button_text}
            <ExternalLink size={12} />
          </button>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Dismiss banner"
        data-testid="top-banner-dismiss"
      >
        <X size={18} />
      </button>
    </div>
  );
};

// Country list with calling codes
const COUNTRIES = [
  { name: "Afghanistan", code: "+93" },
  { name: "Albania", code: "+355" },
  { name: "Algeria", code: "+213" },
  { name: "Andorra", code: "+376" },
  { name: "Angola", code: "+244" },
  { name: "Antigua and Barbuda", code: "+1-268" },
  { name: "Argentina", code: "+54" },
  { name: "Armenia", code: "+374" },
  { name: "Australia", code: "+61" },
  { name: "Austria", code: "+43" },
  { name: "Azerbaijan", code: "+994" },
  { name: "Bahamas", code: "+1-242" },
  { name: "Bahrain", code: "+973" },
  { name: "Bangladesh", code: "+880" },
  { name: "Barbados", code: "+1-246" },
  { name: "Belarus", code: "+375" },
  { name: "Belgium", code: "+32" },
  { name: "Belize", code: "+501" },
  { name: "Benin", code: "+229" },
  { name: "Bhutan", code: "+975" },
  { name: "Bolivia", code: "+591" },
  { name: "Bosnia and Herzegovina", code: "+387" },
  { name: "Botswana", code: "+267" },
  { name: "Brazil", code: "+55" },
  { name: "Brunei", code: "+673" },
  { name: "Bulgaria", code: "+359" },
  { name: "Burkina Faso", code: "+226" },
  { name: "Burundi", code: "+257" },
  { name: "Cabo Verde", code: "+238" },
  { name: "Cambodia", code: "+855" },
  { name: "Cameroon", code: "+237" },
  { name: "Canada", code: "+1" },
  { name: "Central African Republic", code: "+236" },
  { name: "Chad", code: "+235" },
  { name: "Chile", code: "+56" },
  { name: "China", code: "+86" },
  { name: "Colombia", code: "+57" },
  { name: "Comoros", code: "+269" },
  { name: "Congo", code: "+242" },
  { name: "Costa Rica", code: "+506" },
  { name: "Croatia", code: "+385" },
  { name: "Cuba", code: "+53" },
  { name: "Cyprus", code: "+357" },
  { name: "Czech Republic", code: "+420" },
  { name: "Denmark", code: "+45" },
  { name: "Djibouti", code: "+253" },
  { name: "Dominica", code: "+1-767" },
  { name: "Dominican Republic", code: "+1-809" },
  { name: "Ecuador", code: "+593" },
  { name: "Egypt", code: "+20" },
  { name: "El Salvador", code: "+503" },
  { name: "Equatorial Guinea", code: "+240" },
  { name: "Eritrea", code: "+291" },
  { name: "Estonia", code: "+372" },
  { name: "Eswatini", code: "+268" },
  { name: "Ethiopia", code: "+251" },
  { name: "Fiji", code: "+679" },
  { name: "Finland", code: "+358" },
  { name: "France", code: "+33" },
  { name: "Gabon", code: "+241" },
  { name: "Gambia", code: "+220" },
  { name: "Georgia", code: "+995" },
  { name: "Germany", code: "+49" },
  { name: "Ghana", code: "+233" },
  { name: "Greece", code: "+30" },
  { name: "Grenada", code: "+1-473" },
  { name: "Guatemala", code: "+502" },
  { name: "Guinea", code: "+224" },
  { name: "Guinea-Bissau", code: "+245" },
  { name: "Guyana", code: "+592" },
  { name: "Haiti", code: "+509" },
  { name: "Honduras", code: "+504" },
  { name: "Hungary", code: "+36" },
  { name: "Iceland", code: "+354" },
  { name: "India", code: "+91" },
  { name: "Indonesia", code: "+62" },
  { name: "Iran", code: "+98" },
  { name: "Iraq", code: "+964" },
  { name: "Ireland", code: "+353" },
  { name: "Israel", code: "+972" },
  { name: "Italy", code: "+39" },
  { name: "Jamaica", code: "+1-876" },
  { name: "Japan", code: "+81" },
  { name: "Jordan", code: "+962" },
  { name: "Kazakhstan", code: "+7" },
  { name: "Kenya", code: "+254" },
  { name: "Kiribati", code: "+686" },
  { name: "Kosovo", code: "+383" },
  { name: "Kuwait", code: "+965" },
  { name: "Kyrgyzstan", code: "+996" },
  { name: "Laos", code: "+856" },
  { name: "Latvia", code: "+371" },
  { name: "Lebanon", code: "+961" },
  { name: "Lesotho", code: "+266" },
  { name: "Liberia", code: "+231" },
  { name: "Libya", code: "+218" },
  { name: "Liechtenstein", code: "+423" },
  { name: "Lithuania", code: "+370" },
  { name: "Luxembourg", code: "+352" },
  { name: "Madagascar", code: "+261" },
  { name: "Malawi", code: "+265" },
  { name: "Malaysia", code: "+60" },
  { name: "Maldives", code: "+960" },
  { name: "Mali", code: "+223" },
  { name: "Malta", code: "+356" },
  { name: "Marshall Islands", code: "+692" },
  { name: "Mauritania", code: "+222" },
  { name: "Mauritius", code: "+230" },
  { name: "Mexico", code: "+52" },
  { name: "Micronesia", code: "+691" },
  { name: "Moldova", code: "+373" },
  { name: "Monaco", code: "+377" },
  { name: "Mongolia", code: "+976" },
  { name: "Montenegro", code: "+382" },
  { name: "Morocco", code: "+212" },
  { name: "Mozambique", code: "+258" },
  { name: "Myanmar", code: "+95" },
  { name: "Namibia", code: "+264" },
  { name: "Nauru", code: "+674" },
  { name: "Nepal", code: "+977" },
  { name: "Netherlands", code: "+31" },
  { name: "New Zealand", code: "+64" },
  { name: "Nicaragua", code: "+505" },
  { name: "Niger", code: "+227" },
  { name: "Nigeria", code: "+234" },
  { name: "North Korea", code: "+850" },
  { name: "North Macedonia", code: "+389" },
  { name: "Norway", code: "+47" },
  { name: "Oman", code: "+968" },
  { name: "Pakistan", code: "+92" },
  { name: "Palau", code: "+680" },
  { name: "Palestine", code: "+970" },
  { name: "Panama", code: "+507" },
  { name: "Papua New Guinea", code: "+675" },
  { name: "Paraguay", code: "+595" },
  { name: "Peru", code: "+51" },
  { name: "Philippines", code: "+63" },
  { name: "Poland", code: "+48" },
  { name: "Portugal", code: "+351" },
  { name: "Qatar", code: "+974" },
  { name: "Romania", code: "+40" },
  { name: "Russia", code: "+7" },
  { name: "Rwanda", code: "+250" },
  { name: "Saint Kitts and Nevis", code: "+1-869" },
  { name: "Saint Lucia", code: "+1-758" },
  { name: "Saint Vincent and the Grenadines", code: "+1-784" },
  { name: "Samoa", code: "+685" },
  { name: "San Marino", code: "+378" },
  { name: "Sao Tome and Principe", code: "+239" },
  { name: "Saudi Arabia", code: "+966" },
  { name: "Senegal", code: "+221" },
  { name: "Serbia", code: "+381" },
  { name: "Seychelles", code: "+248" },
  { name: "Sierra Leone", code: "+232" },
  { name: "Singapore", code: "+65" },
  { name: "Slovakia", code: "+421" },
  { name: "Slovenia", code: "+386" },
  { name: "Solomon Islands", code: "+677" },
  { name: "Somalia", code: "+252" },
  { name: "South Africa", code: "+27" },
  { name: "South Korea", code: "+82" },
  { name: "South Sudan", code: "+211" },
  { name: "Spain", code: "+34" },
  { name: "Sri Lanka", code: "+94" },
  { name: "Sudan", code: "+249" },
  { name: "Suriname", code: "+597" },
  { name: "Sweden", code: "+46" },
  { name: "Switzerland", code: "+41" },
  { name: "Syria", code: "+963" },
  { name: "Taiwan", code: "+886" },
  { name: "Tajikistan", code: "+992" },
  { name: "Tanzania", code: "+255" },
  { name: "Thailand", code: "+66" },
  { name: "Timor-Leste", code: "+670" },
  { name: "Togo", code: "+228" },
  { name: "Tonga", code: "+676" },
  { name: "Trinidad and Tobago", code: "+1-868" },
  { name: "Tunisia", code: "+216" },
  { name: "Turkey", code: "+90" },
  { name: "Turkmenistan", code: "+993" },
  { name: "Tuvalu", code: "+688" },
  { name: "Uganda", code: "+256" },
  { name: "Ukraine", code: "+380" },
  { name: "United Arab Emirates", code: "+971" },
  { name: "United Kingdom", code: "+44" },
  { name: "United States", code: "+1" },
  { name: "Uruguay", code: "+598" },
  { name: "Uzbekistan", code: "+998" },
  { name: "Vanuatu", code: "+678" },
  { name: "Vatican City", code: "+379" },
  { name: "Venezuela", code: "+58" },
  { name: "Vietnam", code: "+84" },
  { name: "Yemen", code: "+967" },
  { name: "Zambia", code: "+260" },
  { name: "Zimbabwe", code: "+263" }
];

// Helper to get country code by name
const getCountryCode = (countryName) => {
  const country = COUNTRIES.find(c => c.name === countryName);
  return country ? country.code : "";
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Navbar Component
const Navbar = ({ scrollToSection, theme, toggleTheme, topOffset = 0 }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", id: "services" },
    { name: "Monetization", id: "monetization" },
    { name: "Benefits", id: "benefits" },
    { name: "Pricing", id: "pricing" },
    { name: "Success Stories", id: "testimonials" },
    { name: "Apply", id: "apply" }
  ];

  return (
    <>
      <nav 
        style={{ top: `${topOffset}px` }}
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? theme === 'dark' 
              ? "bg-black/80 backdrop-blur-xl border-b border-white/5 py-4" 
              : "bg-white/80 backdrop-blur-xl border-b border-black/5 py-4"
            : "py-6"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="#" className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }} data-testid="logo">
            <span className="text-[var(--foreground)]">HotPeek</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="nav-link text-sm font-medium"
                data-testid={`nav-${link.id}`}
              >
                {link.name}
              </button>
            ))}
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-full transition-colors ${
                theme === 'dark' 
                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                  : 'bg-black/5 hover:bg-black/10 text-gray-700'
              }`}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <button
              onClick={() => scrollToSection("apply")}
              className="brand-gradient text-white px-6 py-2.5 rounded-full font-semibold text-sm glow-blue"
              data-testid="nav-apply-cta"
            >
              Apply Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${
                theme === 'dark' 
                  ? 'bg-white/10 text-white' 
                  : 'bg-black/5 text-gray-700'
              }`}
              data-testid="theme-toggle-mobile"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button 
              className="text-[var(--foreground)]"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-12">
            <span className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
              <span className="text-[var(--foreground)]">HotPeek</span>
            </span>
            <button onClick={() => setMobileMenuOpen(false)} className="text-[var(--foreground)]" data-testid="close-mobile-menu">
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => {
                  scrollToSection(link.id);
                  setMobileMenuOpen(false);
                }}
                className="text-2xl font-semibold text-left text-[var(--foreground)]"
                data-testid={`mobile-nav-${link.id}`}
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={() => {
                scrollToSection("apply");
                setMobileMenuOpen(false);
              }}
              className="brand-gradient text-white px-8 py-4 rounded-full font-semibold text-lg mt-4 glow-blue"
              data-testid="mobile-apply-cta"
            >
              Apply Now
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection, theme }) => {
  return (
    <section className="hero-bg min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-6 sm:space-y-8"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[var(--primary)] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase"
            >
              Elite Creator Management
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.1] text-[var(--foreground)]"
            >
              Unlock Your{" "}
              <span className="brand-gradient-text">Full Potential.</span>
              <br />
              Maximize Your{" "}
              <span className="text-gradient">Earnings.</span>
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg md:text-xl leading-relaxed text-[var(--muted)] max-w-lg"
            >
              Hotpeek is the elite management agency for top-tier creators. 
              We handle the business, you create the art.
            </motion.p>

            {/* Feedspot Badge */}
            <motion.a
              variants={fadeInUp}
              href="https://influencers.feedspot.com/onlyfans-agencies/"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-3 px-4 py-3 rounded-xl border transition-all hover:scale-[1.02] ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 hover:border-amber-500/50'
                  : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 hover:border-amber-400 shadow-sm'
              }`}
              data-testid="feedspot-badge"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <Star size={20} fill="white" />
              </div>
              <div className="text-left">
                <p className={`text-xs font-medium ${theme === 'dark' ? 'text-amber-400' : 'text-amber-600'}`}>
                  Featured on Feedspot
                </p>
                <p className="text-sm font-bold text-[var(--foreground)]">
                  Top OnlyFans Agencies 2026
                </p>
              </div>
            </motion.a>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => scrollToSection("apply")}
                className="brand-gradient text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg glow-blue flex items-center justify-center gap-2 group"
                data-testid="hero-apply-btn"
              >
                Apply Now
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection("services")}
                className={`border px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-colors ${
                  theme === 'dark' 
                    ? 'border-white/20 text-white hover:bg-white/5' 
                    : 'border-black/20 text-gray-800 hover:bg-black/5'
                }`}
                data-testid="hero-services-btn"
              >
                View Services
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 sm:gap-6 pt-6 sm:pt-8">
              <div className={`px-4 py-2 rounded-full ${
                theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
              }`}>
                <p className="text-[var(--foreground)] text-sm sm:text-base font-medium">Limited Creator Slots Available</p>
              </div>
              <div className={`px-4 py-2 rounded-full ${
                theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
              }`}>
                <p className="text-[var(--foreground)] text-sm sm:text-base font-medium">Daily Content Optimization</p>
              </div>
              <div className={`px-4 py-2 rounded-full ${
                theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
              }`}>
                <p className="text-[var(--foreground)] text-sm sm:text-base font-medium">Performance-Driven</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="image-glow-container">
              <img
                src="https://images.pexels.com/photos/2787341/pexels-photo-2787341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional model"
                className="w-full h-auto rounded-2xl object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Growth Marketing Section
const GrowthMarketingSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const growthFeatures = [
    { icon: <Users size={24} />, title: "Targeted Audience", desc: "We reach real fans actively looking for content like yours" },
    { icon: <TrendingUp size={24} />, title: "High-Traffic Exposure", desc: "Get featured on platforms with millions of daily visitors" },
    { icon: <Sparkles size={24} />, title: "Proven Growth Strategies", desc: "Battle-tested methods that consistently deliver results" },
    { icon: <Zap size={24} />, title: "Round-the-Clock Promotion", desc: "Your content gets pushed 24/7, even while you sleep" }
  ];

  return (
    <section id="growth" className="py-24 px-6 cta-bg" data-testid="growth-section" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[var(--primary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              Explosive Growth
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)] mb-6">
              We Boost Your Revenue With{" "}
              <span className="brand-gradient-text">Explosive Marketing</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[var(--muted)] leading-relaxed mb-8">
              No bots. No fake followers. Just <strong className="text-[var(--foreground)]">real, organic growth</strong> that 
              brings genuine fans who <em>actually want</em> to see your content. 
              We use targeted marketing strategies to attract the right audience — people who engage, tip, and subscribe month after month.
            </motion.p>

            <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 gap-4">
              {growthFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title || `growth-${index}`}
                  variants={fadeInUp}
                  className={`p-4 rounded-xl flex items-start gap-4 ${
                    theme === 'dark'
                      ? 'bg-white/5 border border-white/10'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                  data-testid={`growth-feature-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg brand-gradient flex items-center justify-center text-white flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--foreground)]">{feature.title}</p>
                    <p className="text-sm text-[var(--muted)]">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block"
          >
            <div className={`relative p-8 rounded-3xl ${
              theme === 'dark'
                ? 'bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-white/10'
                : 'bg-white border border-gray-200 shadow-2xl'
            }`}>
              {/* Growth chart visualization */}
              <div className="text-center mb-6">
                <p className="text-sm text-[var(--muted)] mb-2">Average Creator Growth</p>
                <p className="stat-number text-5xl md:text-6xl">+340%</p>
                <p className="text-[var(--muted)] text-sm">in first 90 days</p>
              </div>
              
              {/* Visual bars */}
              <div className="flex items-end justify-center gap-3 h-40">
                <div className={`w-12 rounded-t-lg ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-100'}`} style={{ height: '30%' }}>
                  <div className="text-center mt-2 text-xs text-[var(--muted)]">Month 1</div>
                </div>
                <div className={`w-12 rounded-t-lg ${theme === 'dark' ? 'bg-white/20' : 'bg-gray-200'}`} style={{ height: '50%' }}>
                  <div className="text-center mt-2 text-xs text-[var(--muted)]">Month 2</div>
                </div>
                <div className="w-12 rounded-t-lg brand-gradient" style={{ height: '85%' }}>
                  <div className="text-center mt-2 text-xs text-white">Month 3</div>
                </div>
                <div className="w-12 rounded-t-lg brand-gradient opacity-80" style={{ height: '100%' }}>
                  <div className="text-center mt-2 text-xs text-white">Month 4</div>
                </div>
              </div>
              
              {/* Stats row */}
              <div className="flex justify-around mt-8 pt-6 border-t border-[var(--border)]">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[var(--foreground)]">10K+</p>
                  <p className="text-xs text-[var(--muted)]">New Fans/Month</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[var(--foreground)]">98%</p>
                  <p className="text-xs text-[var(--muted)]">Real Engagement</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[var(--foreground)]">5x</p>
                  <p className="text-xs text-[var(--muted)]">ROI Average</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: <Zap size={24} className="text-[var(--primary)]" />,
      title: "Account Management",
      description: "Full management of your creator account. We optimize your profile, handle subscriptions, and maximize your visibility."
    },
    {
      icon: <MessageCircle size={24} className="text-[var(--secondary)]" />,
      title: "24/7 Chat Support",
      description: "Our team handles all fan interactions professionally, keeping your subscribers engaged and spending."
    },
    {
      icon: <TrendingUp size={24} className="text-[var(--primary)]" />,
      title: "Viral Marketing Strategy",
      description: "Custom promotion strategies across social platforms. We drive traffic and grow your fanbase exponentially."
    },
    {
      icon: <Camera size={24} className="text-[var(--secondary)]" />,
      title: "Content Optimization",
      description: "Expert guidance on content that converts. We analyze trends and help you create what fans actually want."
    },
    {
      icon: <ShieldCheck size={24} className="text-[var(--primary)]" />,
      title: "Legal & DMCA Protection",
      description: "We protect your content from piracy and handle all legal matters, so you can focus on creating."
    },
    {
      icon: <DollarSign size={24} className="text-[var(--secondary)]" />,
      title: "Revenue Maximization",
      description: "Strategic pricing, PPV optimization, and tip menus designed to maximize your earnings potential."
    }
  ];

  return (
    <section id="services" className="py-24 px-6" data-testid="services-section" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-[var(--primary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            What We Offer
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)]">
            Elite Management Services
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="bento-grid"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title || `service-${index}`}
              variants={fadeInUp}
              className="feature-card"
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon mb-6">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[var(--foreground)]">{service.title}</h3>
              <p className="text-[var(--muted)] leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Freedom Section - No Office
const FreedomSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const freedomPoints = [
    { icon: <Clock size={24} />, title: "Work When You Want", desc: "No alarm clocks, no schedules. Create on your own terms." },
    { icon: <MapPin size={24} />, title: "Work From Anywhere", desc: "Beach, mountains, or your couch. Your office is wherever you are." },
    { icon: <Plane size={24} />, title: "Travel The World", desc: "Turn any destination into your workspace. Live like you're on vacation." },
    { icon: <Heart size={24} />, title: "Live Life Fully", desc: "Spend time with loved ones. Experience life, don't just work through it." },
    { icon: <ShieldCheck size={24} />, title: "Stay Anonymous", desc: "Build a global fanbase while maintaining complete privacy in your home country." }
  ];

  return (
    <section id="freedom" className="py-24 px-6 relative overflow-hidden" data-testid="freedom-section" ref={ref}>
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10' 
            : 'bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5'
        } blur-3xl`} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-[var(--secondary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Freedom Awaits
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-6xl font-bold tracking-tight text-[var(--foreground)] mb-6">
            Forget About Working<br />
            <span className="brand-gradient-text">In An Office.</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[var(--muted)] max-w-3xl mx-auto leading-relaxed">
            While others are stuck in traffic and cubicles, you'll be living life on your terms. 
            No boss. No commute. No limits. Just you, your creativity, and endless possibilities.
          </motion.p>
        </motion.div>

        {/* Freedom Points Grid */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12"
        >
          {freedomPoints.map((point, index) => (
            <motion.div
              key={point.title || `freedom-${index}`}
              variants={fadeInUp}
              className={`p-6 rounded-2xl text-center ${
                theme === 'dark' 
                  ? 'bg-white/5 border border-white/10' 
                  : 'bg-black/[0.02] border border-black/5'
              } hover:border-[var(--primary)]/30 transition-colors`}
              data-testid={`freedom-point-${index}`}
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl brand-gradient flex items-center justify-center text-white">
                {point.icon}
              </div>
              <h3 className="text-lg font-semibold mb-2 text-[var(--foreground)]">{point.title}</h3>
              <p className="text-[var(--muted)] text-sm">{point.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Privacy Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={`p-6 md:p-8 rounded-2xl mb-16 flex flex-col md:flex-row items-center gap-6 ${
            theme === 'dark' 
              ? 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-white/10' 
              : 'bg-gradient-to-r from-purple-500/5 to-blue-500/5 border border-black/5'
          }`}
          data-testid="privacy-highlight"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center flex-shrink-0">
            <Globe size={32} className="text-white" />
          </div>
          <div className="text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-[var(--foreground)] mb-2">
              Your Identity, Your Rules
            </h3>
            <p className="text-[var(--muted)] leading-relaxed">
              Build a thriving international career without anyone in your neighborhood ever knowing. 
              Our creators earn from fans across every continent while keeping their real identity completely separate. 
              Your success story stays yours to share — or not.
            </p>
          </div>
        </motion.div>

        {/* Quote/Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className={`relative p-8 md:p-12 rounded-3xl ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-white/10' 
              : 'bg-gradient-to-br from-white to-gray-50 border border-black/5 shadow-xl'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-2xl md:text-3xl font-bold text-[var(--foreground)] leading-tight mb-4">
                "Life is too short to spend it in a{" "}
                <span className={theme === 'dark' ? 'line-through opacity-50' : 'line-through opacity-40'}>cubicle</span>.{" "}
                <span className="brand-gradient-text">Create your own path.</span>"
              </p>
              <p className="text-[var(--muted)]">
                Join thousands of creators who said goodbye to the 9-to-5 and hello to financial freedom, 
                flexibility, and a life worth living.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <div className="text-center p-4">
                <p className="stat-number text-3xl">90%</p>
                <p className="text-[var(--muted)] text-sm">Work from home</p>
              </div>
              <div className="text-center p-4">
                <p className="stat-number text-3xl">4hrs</p>
                <p className="text-[var(--muted)] text-sm">Average workday</p>
              </div>
              <div className="text-center p-4">
                <p className="stat-number text-3xl">100%</p>
                <p className="text-[var(--muted)] text-sm">Your schedule</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Monetization Platform Section
const MonetizationSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const advantages = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Built-In Creator Promotion",
      description: "The platform is designed not only for monetisation, but also for creator growth and exposure. We actively help creators gain visibility and attract new fans through platform promotion and marketing activities."
    },
    {
      icon: <Unlock className="w-8 h-8" />,
      title: "Direct Pay-to-Unlock Monetisation",
      description: "Creators can monetize every content item individually by setting custom credit prices for each photo, video, or premium post — giving full control over earnings and pricing strategy."
    },
    {
      icon: <Coins className="w-8 h-8" />,
      title: "Flexible Credit-Based Economy",
      description: "Fans purchase credits once and spend them seamlessly across content, creating a smoother user experience and increasing purchase conversions."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "No Subscription Barrier",
      description: "Fans can instantly unlock content without committing to monthly subscriptions, making it easier for new users to become paying customers."
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Scalable Revenue Opportunities",
      description: "Creators can continuously generate revenue through exclusive drops, premium galleries, locked content, and special releases — maximizing fan lifetime value."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Cross-Creator Discovery & Engagement",
      description: "Fans can discover and support multiple creators within the same ecosystem, helping creators receive additional organic exposure and engagement through the platform itself."
    }
  ];

  return (
    <section 
      id="monetization" 
      className={`py-24 relative overflow-hidden ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-purple-900/20 via-[#0A0A0A] to-pink-900/20' 
          : 'bg-gradient-to-br from-purple-50 via-white to-pink-50'
      }`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            theme === 'dark'
              ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30'
              : 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600 border border-purple-200'
          }`}>
            <Sparkles className="w-4 h-4 inline mr-2" />
            Exclusive Platform
          </span>
          
          <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Boost Your Income With Our
            <span className="block bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              Monetization Platform
            </span>
          </h2>
          
          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Our cutting-edge platform is designed to maximize your earnings. With powerful tools and 
            features built specifically for content creators, you'll have everything you need to 
            turn your passion into profit.
          </p>
        </motion.div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={`rounded-2xl p-8 mb-16 ${
            theme === 'dark'
              ? 'bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30'
              : 'bg-gradient-to-r from-purple-500 to-pink-500'
          }`}
        >
          <div className="grid grid-cols-2 gap-8 text-center max-w-xl mx-auto">
            {[
              { value: "3x", label: "Average Earnings Boost" },
              { value: "24/7", label: "Platform Support" }
            ].map((stat, index) => (
              <div key={index}>
                <div className={`text-3xl md:text-4xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-white'
                }`}>
                  {stat.value}
                </div>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-white/80'
                }`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((advantage, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`group relative p-6 rounded-2xl transition-all duration-300 hover:scale-[1.02] ${
                theme === 'dark'
                  ? 'bg-white/5 border border-white/10 hover:border-purple-500/50 hover:bg-white/10'
                  : 'bg-white border border-gray-200 hover:border-purple-300 hover:shadow-xl'
              }`}
            >
              {/* Icon */}
              <div className={`inline-flex p-3 rounded-xl mb-4 ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 text-purple-400'
                  : 'bg-gradient-to-br from-purple-100 to-pink-100 text-purple-600'
              }`}>
                {advantage.icon}
              </div>

              {/* Content */}
              <h3 className={`text-xl font-semibold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {advantage.title}
              </h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {advantage.description}
              </p>

              {/* Hover Gradient */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105"
          >
            Start Earning More Today
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselImages = [
    {
      url: "https://images.unsplash.com/photo-1769149255642-c9741ad91b05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBiZWFjaCUyMHZhY2F0aW9uJTIwcmVzb3J0JTIwcG9vbHxlbnwwfHx8fDE3NzMzNjA3ODF8MA&ixlib=rb-4.1.0&q=85",
      alt: "Infinity pool at sunset"
    },
    {
      url: "https://images.unsplash.com/photo-1760564019141-abe33455ce02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBiZWFjaCUyMHZhY2F0aW9uJTIwcmVzb3J0JTIwcG9vbHxlbnwwfHx8fDE3NzMzNjA3ODF8MA&ixlib=rb-4.1.0&q=85",
      alt: "Turquoise ocean infinity pool"
    },
    {
      url: "https://images.unsplash.com/photo-1698809659883-2b2e453cdbea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaWZlc3R5bGUlMjB3b21hbiUyMGluZmx1ZW5jZXJ8ZW58MHx8fHwxNzczMzYwNzcxfDA&ixlib=rb-4.1.0&q=85",
      alt: "Luxury lifestyle"
    },
    {
      url: "https://images.unsplash.com/photo-1762778013301-792c66e710a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiZWFjaCUyMHZhY2F0aW9uJTIwcmVzb3J0JTIwcG9vbHxlbnwwfHx8fDE3NzMzNjA3ODF8MA&ixlib=rb-4.1.0&q=85",
      alt: "Resort pool with mountains"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const benefits = [
    "Industry-leading revenue share - you keep the majority",
    "Personal manager assigned to your account",
    "Professional content strategy and guidance",
    "Growth guaranteed or we work for free",
    "Access to exclusive creator community",
    "Weekly payouts with no delays"
  ];

  return (
    <section id="benefits" className="py-24 px-6" data-testid="benefits-section" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Carousel */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="image-glow-container">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                {carouselImages.map((image, index) => (
                  <motion.img
                    key={image.url || `carousel-img-${index}`}
                    src={image.url}
                    alt={image.alt}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: currentSlide === index ? 1 : 0,
                      scale: currentSlide === index ? 1 : 1.1
                    }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                    data-testid={`carousel-image-${index}`}
                  />
                ))}
              </div>
              
              {/* Carousel Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {carouselImages.map((_, index) => (
                  <button
                    key={`carousel-dot-${index}`}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentSlide === index 
                        ? 'w-8 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)]' 
                        : theme === 'dark' ? 'bg-white/30' : 'bg-black/20'
                    }`}
                    data-testid={`carousel-dot-${index}`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.p variants={fadeInUp} className="text-[var(--secondary)] text-sm font-semibold tracking-[0.2em] uppercase">
              Why Choose Us
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)]">
              Live The Life<br />
              <span className="brand-gradient-text">You Deserve</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[var(--muted)] leading-relaxed">
              Join hundreds of successful creators who transformed their careers with Hotpeek. 
              We provide everything you need to succeed - so you can focus on what you do best.
            </motion.p>

            <motion.div variants={staggerContainer} className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={`benefit-${index}`}
                  variants={fadeInUp}
                  className="flex items-center gap-4"
                  data-testid={`benefit-${index}`}
                >
                  <div className="w-6 h-6 rounded-full brand-gradient flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-white" />
                  </div>
                  <p className="text-[var(--foreground)]">{benefit}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ theme, scrollToSection }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const commissionFeatures = [
    { icon: <ShieldCheck size={20} />, title: "Zero Risk", desc: "No upfront fees, ever" },
    { icon: <Wallet size={20} />, title: "You Stay in Control", desc: "Your account, your payouts" },
    { icon: <TrendingUp size={20} />, title: "We Grow Together", desc: "Our success is tied to yours" },
    { icon: <Sparkles size={20} />, title: "Uncapped Earnings", desc: "No income limits" },
    { icon: <Users size={20} />, title: "Complete Management", desc: "We handle everything" },
    { icon: <Check size={20} />, title: "Your Terms", desc: "Plans built for you" }
  ];

  return (
    <section id="pricing" className="py-24 px-6" data-testid="pricing-section" ref={ref}>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.p variants={fadeInUp} className="text-[var(--primary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Simple Pricing
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)] mb-4">
            Commission Based
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[var(--muted)] max-w-2xl mx-auto">
            We only earn when you earn. Zero risk, maximum potential.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className={`relative p-8 md:p-12 rounded-3xl overflow-hidden ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-white/10'
              : 'bg-white border border-gray-200 shadow-xl'
          }`}
          data-testid="pricing-commission"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {commissionFeatures.map((feature, index) => (
              <motion.div 
                key={feature.title || `commission-${index}`} 
                className="flex items-start gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
              >
                <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center flex-shrink-0 text-white">
                  {feature.icon}
                </div>
                <div>
                  <p className="font-semibold text-[var(--foreground)] text-lg">{feature.title}</p>
                  <p className="text-[var(--muted)]">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <button
            onClick={() => scrollToSection("apply")}
            className="w-full brand-gradient text-white py-5 rounded-full font-bold text-lg glow-blue flex items-center justify-center gap-2 group"
            data-testid="pricing-commission-cta"
          >
            Start Earning Today
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Bottom note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center text-[var(--muted)] text-sm mt-8"
        >
          Apply now and we'll discuss the details that work best for you.
        </motion.p>
      </div>
    </section>
  );
};

// Referral Section
const ReferralSection = ({ theme, scrollToSection }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const referralBenefits = [
    { icon: <DollarSign size={20} />, text: "Earn commission on every friend who joins" },
    { icon: <Users size={20} />, text: "No limit on how many friends you can refer" },
    { icon: <Gift size={20} />, text: "Bonuses for top referrers each month" },
    { icon: <Zap size={20} />, text: "Instant tracking of your referrals" }
  ];

  return (
    <section id="referral" className="py-24 px-6 relative overflow-hidden" data-testid="referral-section" ref={ref}>
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-full h-full ${
          theme === 'dark'
            ? 'bg-gradient-to-b from-purple-500/5 via-transparent to-transparent'
            : 'bg-gradient-to-b from-purple-500/[0.03] via-transparent to-transparent'
        }`} />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className={`p-8 md:p-12 rounded-3xl ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-white/10'
              : 'bg-white border border-gray-200 shadow-2xl'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left Content */}
            <div>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--secondary)] to-purple-600 flex items-center justify-center mb-6">
                <UserPlus size={32} className="text-white" />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--foreground)] mb-4">
                Earn Money By Referring{" "}
                <span className="brand-gradient-text">Your Friends!</span>
              </h2>
              
              <p className="text-lg text-[var(--muted)] mb-8 leading-relaxed">
                Know someone who'd be perfect for Hotpeek? Share the opportunity and get rewarded. 
                Every successful referral puts money in your pocket — it's that simple.
              </p>

              <div className="space-y-4 mb-8">
                {referralBenefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.text || `referral-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[var(--secondary)]/10 flex items-center justify-center text-[var(--secondary)]">
                      {benefit.icon}
                    </div>
                    <p className="text-[var(--foreground)]">{benefit.text}</p>
                  </motion.div>
                ))}
              </div>

              <button
                onClick={() => scrollToSection("apply")}
                className="brand-gradient text-white px-8 py-4 rounded-full font-bold text-lg glow-purple flex items-center gap-2 group"
                data-testid="referral-cta"
              >
                Start Earning Today
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Right Side - Visual */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                {/* Decorative circles */}
                <div className={`absolute -top-8 -left-8 w-32 h-32 rounded-full ${
                  theme === 'dark' ? 'bg-[var(--primary)]/10' : 'bg-[var(--primary)]/5'
                } animate-pulse`} />
                <div className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-full ${
                  theme === 'dark' ? 'bg-[var(--secondary)]/10' : 'bg-[var(--secondary)]/5'
                } animate-pulse`} style={{ animationDelay: '1s' }} />
                
                {/* Main visual */}
                <div className={`relative z-10 p-8 rounded-2xl ${
                  theme === 'dark' ? 'bg-white/5' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full brand-gradient flex items-center justify-center text-white text-xl font-bold">
                      You
                    </div>
                    <ArrowRight size={24} className="text-[var(--muted)]" />
                    <div className="flex -space-x-3">
                      <div className="w-12 h-12 rounded-full bg-[var(--primary)] flex items-center justify-center text-white text-sm font-bold border-2 border-[var(--background)]">
                        +1
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[var(--secondary)] flex items-center justify-center text-white text-sm font-bold border-2 border-[var(--background)]">
                        +2
                      </div>
                      <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold border-2 border-[var(--background)]">
                        +3
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="stat-number text-4xl mb-1">$$$</p>
                    <p className="text-[var(--muted)] text-sm">More Friends = More Earnings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Additional Revenue Section
const AdditionalRevenueSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const revenueStreams = [
    { icon: <Camera size={24} />, title: "Brand Collaborations", desc: "Partner with premium brands for sponsored content" },
    { icon: <MessageCircle size={24} />, title: "Private Platforms", desc: "Expand to exclusive messaging apps and platforms" },
    { icon: <Gift size={24} />, title: "Merchandise", desc: "Create and sell your own branded products" },
    { icon: <Star size={24} />, title: "Premium Content", desc: "Exclusive content on multiple platforms" },
    { icon: <Users size={24} />, title: "Fan Clubs", desc: "Build loyal communities that pay monthly" },
    { icon: <Zap size={24} />, title: "Live Events", desc: "Monetize live streaming and virtual events" }
  ];

  return (
    <section id="revenue" className="py-24 px-6" data-testid="revenue-section" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-[var(--secondary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Beyond OnlyFans
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)] mb-6">
            We Unlock Additional Revenue Streams{" "}
            <span className="brand-gradient-text">Outside of OnlyFans</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[var(--muted)] max-w-3xl mx-auto">
            OnlyFans is just the beginning. We help you diversify your income across multiple platforms 
            and opportunities — so you're never dependent on a single source.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {revenueStreams.map((stream, index) => (
            <motion.div
              key={stream.title || `stream-${index}`}
              variants={fadeInUp}
              className={`p-6 rounded-2xl ${
                theme === 'dark'
                  ? 'bg-[#0A0A0A] border border-white/10 hover:border-[var(--secondary)]/30'
                  : 'bg-white border border-gray-200 hover:border-[var(--secondary)]/30 shadow-sm'
              } transition-all duration-300 hover:-translate-y-1`}
              data-testid={`revenue-stream-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--secondary)]/20 to-purple-600/20 flex items-center justify-center text-[var(--secondary)] mb-4">
                {stream.icon}
              </div>
              <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">{stream.title}</h3>
              <p className="text-[var(--muted)] text-sm">{stream.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.5 }}
          className={`mt-12 p-8 rounded-2xl text-center ${
            theme === 'dark'
              ? 'bg-gradient-to-r from-[var(--secondary)]/10 to-purple-600/10 border border-white/10'
              : 'bg-gradient-to-r from-[var(--secondary)]/5 to-purple-600/5 border border-black/5'
          }`}
        >
          <p className="text-xl font-semibold text-[var(--foreground)] mb-2">
            Why limit yourself to one platform?
          </p>
          <p className="text-[var(--muted)]">
            Our creators earn <span className="text-[var(--secondary)] font-semibold">significantly more</span> by 
            leveraging multiple revenue streams. Let us show you how.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      name: "Jessica M.",
      earnings: "Before $300 → Now $4K",
      quote: "Before HotPeek, I was barely making $300. Now I consistently hit $4K+ monthly. They changed my life.",
      avatar: "JM"
    },
    {
      name: "Sarah K.",
      earnings: "$7K/month",
      quote: "The chat team is incredible. My engagement went up 400% and I don't even have to answer messages anymore.",
      avatar: "SK"
    },
    {
      name: "Amanda R.",
      earnings: "$6.5K/month",
      quote: "HotPeek's strategy and support made it possible. Best decision I ever made.",
      avatar: "AR"
    }
  ];

  return (
    <section id="testimonials" className="py-24 px-6 cta-bg" data-testid="testimonials-section" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-[var(--primary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Success Stories
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)]">
            Real Creators, Real Results
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name || `testimonial-${index}`}
              variants={fadeInUp}
              className="testimonial-card"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full brand-gradient flex items-center justify-center font-bold text-white">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-semibold text-[var(--foreground)]">{testimonial.name}</p>
                  <p className="text-[var(--primary)] text-sm font-medium">{testimonial.earnings}</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((starNum) => (
                  <Star key={`star-${starNum}`} size={16} fill="var(--primary)" className="text-[var(--primary)]" />
                ))}
              </div>
              <p className="text-[var(--muted)] leading-relaxed">"{testimonial.quote}"</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How do I get started with Hotpeek?",
      answer: "Simply fill out our application form with your details and photos. Our team will review your application within 24-48 hours and reach out to discuss the next steps. The onboarding process is quick and easy — we'll guide you through everything."
    },
    {
      question: "How much can I realistically earn?",
      answer: "Earnings vary based on your dedication, content quality, and engagement. Our top creators earn $50K+ monthly, while newer creators typically start seeing significant income within their first few months. With our proven strategies and support, we help maximize your earning potential."
    },
    {
      question: "What percentage does HotPeek take?",
      answer: "We work on a commission-based model where you keep the majority of your earnings — one of the best rates in the industry. We only succeed when you succeed, so we're fully invested in growing your account. Apply and we'll discuss the details."
    },
    {
      question: "Do I need experience to join?",
      answer: "No experience necessary! We work with both beginners and established creators. Our team provides comprehensive training, content strategies, and ongoing support to help you succeed regardless of your starting point."
    },
    {
      question: "How do you protect my privacy?",
      answer: "Your privacy is our priority. We help you build a global audience while maintaining complete anonymity in your home country. Our team uses proven strategies to separate your creator identity from your personal life."
    },
    {
      question: "What services are included in the management?",
      answer: "We provide full account management including content strategy, 24/7 chat support with fans, marketing and promotion, DMCA protection, revenue optimization, and a dedicated personal manager. We handle the business so you can focus on creating."
    },
    {
      question: "How often do I get paid?",
      answer: "We process payments weekly with no delays. You'll have full transparency into your earnings through your dashboard, and payments are sent directly to your preferred payment method."
    },
    {
      question: "Can I leave if I'm not satisfied?",
      answer: "Absolutely. We believe in earning your trust through results, not contracts. While we're confident you'll love working with us, you're free to part ways with proper notice as outlined in our agreement."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 px-6" data-testid="faq-section" ref={ref}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-[var(--primary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Got Questions?
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[var(--foreground)]">
            Frequently Asked Questions
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.question || `faq-${index}`}
              variants={fadeInUp}
              className={`rounded-2xl overflow-hidden transition-colors ${
                theme === 'dark'
                  ? 'bg-[#0A0A0A] border border-white/10 hover:border-white/20'
                  : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
              }`}
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-semibold text-[var(--foreground)] pr-4">{faq.question}</span>
                <ChevronDown 
                  size={20} 
                  className={`text-[var(--muted)] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-6 pb-5 text-[var(--muted)] leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Still have questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-[var(--muted)] mb-4">Still have questions?</p>
          <a 
            href="#apply" 
            className="text-[var(--primary)] font-semibold hover:underline inline-flex items-center gap-2"
          >
            Contact us through the application form
            <ArrowRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Application Form Section
const ApplicationSection = ({ theme }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    instagram: "",
    tiktok: "",
    twitter: "",
    onlyfans: "",
    whatsapp: "",
    message: "",
    termsAgreed: false
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "country") {
      // When country changes, update the WhatsApp field with the country code
      const countryCode = getCountryCode(value);
      setFormData(prev => ({
        ...prev,
        country: value,
        whatsapp: countryCode + " "
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAgreed) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("country", formData.country);
      submitData.append("instagram", formData.instagram);
      submitData.append("tiktok", formData.tiktok);
      submitData.append("twitter", formData.twitter);
      submitData.append("onlyfans", formData.onlyfans);
      submitData.append("whatsapp", formData.whatsapp);
      submitData.append("message", formData.message);
      submitData.append("terms_agreed", formData.termsAgreed);
      
      if (photo) {
        submitData.append("photo", photo);
      }

      await axios.post(`${API}/applications`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      setSubmitted(true);
      toast.success("Application submitted successfully! We'll be in touch soon.");
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="apply" className="py-24 px-6" data-testid="apply-section">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 mx-auto mb-8 rounded-full brand-gradient flex items-center justify-center"
          >
            <Check size={48} className="text-white" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4 text-[var(--foreground)]">Application Received!</h2>
          <p className="text-[var(--muted)] text-lg mb-8">
            Thank you for your interest in joining Hotpeek. Our team will review your application 
            and get back to you within 24-48 hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                country: "",
                instagram: "",
                tiktok: "",
                twitter: "",
                onlyfans: "",
                whatsapp: "",
                message: "",
                termsAgreed: false
              });
              setPhoto(null);
              setPhotoPreview(null);
            }}
            className="text-[var(--primary)] font-medium hover:underline"
            data-testid="submit-another-btn"
          >
            Submit another application
          </button>
        </div>
      </section>
    );
  }

  const inputClasses = theme === 'dark' 
    ? "w-full bg-[#1A1A1A] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-colors"
    : "w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-colors";

  return (
    <section id="apply" className="py-24 px-6" data-testid="apply-section" ref={ref}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.p variants={fadeInUp} className="text-[var(--secondary)] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Start Your Journey
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-[var(--foreground)]">
            Join The Elite
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-[var(--muted)] text-lg max-w-2xl mx-auto">
            Ready to transform your creator career? Fill out the form below and our team will 
            reach out within 24 hours.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="form-container p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
            {/* Personal Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="Your name"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="your@email.com"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Country *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  data-testid="input-country"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map(country => (
                    <option key={country.name} value={country.name}>{country.name} ({country.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">WhatsApp Number *</label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="Enter your phone number"
                  data-testid="input-whatsapp"
                />
              </div>
            </div>

            {/* Social Profiles */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">
                  <Instagram size={16} className="inline mr-2" />
                  Instagram Profile
                </label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="@yourusername"
                  data-testid="input-instagram"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">TikTok Profile</label>
                <input
                  type="text"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="@yourusername"
                  data-testid="input-tiktok"
                />
              </div>
            </div>

            {/* Twitter and OnlyFans */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Twitter/X Profile</label>
                <input
                  type="text"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="@yourusername"
                  data-testid="input-twitter"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">OnlyFans Profile</label>
                <input
                  type="text"
                  name="onlyfans"
                  value={formData.onlyfans}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="onlyfans.com/yourusername"
                  data-testid="input-onlyfans"
                />
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Profile Photo</label>
              <div
                className={`file-upload-area ${dragOver ? "dragover" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById("photo-input").click()}
                data-testid="photo-upload-area"
              >
                {photoPreview ? (
                  <div className="flex items-center gap-4">
                    <img src={photoPreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                    <div className="text-left">
                      <p className="text-[var(--foreground)] font-medium">{photo?.name}</p>
                      <p className="text-[var(--muted)] text-sm">Click to change</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto mb-4 text-[var(--muted)]" />
                    <p className="text-[var(--foreground)] mb-1">Drop your photo here or click to upload</p>
                    <p className="text-[var(--muted)] text-sm">PNG, JPG up to 5MB</p>
                  </div>
                )}
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  data-testid="input-photo"
                />
              </div>
              <p className="text-[var(--muted)] text-sm mt-2">
                Add at least one image of you.
              </p>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--foreground)]">Message (Optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className={`${inputClasses} resize-none`}
                placeholder="Tell us about yourself and your experience..."
                data-testid="input-message"
              />
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="termsAgreed"
                checked={formData.termsAgreed}
                onChange={handleChange}
                className="custom-checkbox mt-1"
                data-testid="input-terms"
              />
              <label className="text-[var(--muted)] text-sm">
                I agree to the{" "}
                <Link to="/terms" className="text-[var(--primary)] hover:underline">Terms and Conditions</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-[var(--primary)] hover:underline">Privacy Policy</Link>
                . I understand that Hotpeek will contact me regarding my application. *
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full brand-gradient text-white py-5 rounded-full font-bold text-lg glow-blue disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ theme }) => {
  return (
    <footer className={`py-16 px-6 border-t ${theme === 'dark' ? 'border-[#222]' : 'border-gray-200'}`} data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          <div className="md:col-span-2">
            <Link to="/" className="text-3xl font-bold inline-block mb-4" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
              <span className="text-[var(--foreground)]">HotPeek</span>
            </Link>
            <p className="text-[var(--muted)] max-w-md leading-relaxed">
              The elite management agency for top-tier content creators. 
              We handle the business, you create the art.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-[var(--foreground)]">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#services" className="footer-link">Services</a></li>
              <li><a href="#monetization" className="footer-link">Monetization Platform</a></li>
              <li><a href="#benefits" className="footer-link">Benefits</a></li>
              <li><a href="#pricing" className="footer-link">Pricing</a></li>
              <li><a href="#testimonials" className="footer-link">Success Stories</a></li>
              <li><a href="#apply" className="footer-link">Apply Now</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-[var(--foreground)]">Account</h4>
            <ul className="space-y-3">
              <li><Link to="/login" className="footer-link" data-testid="footer-login-link">Login</Link></li>
              <li><Link to="/register" className="footer-link" data-testid="footer-register-link">Register</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-[var(--foreground)]">Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/privacy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="footer-link" data-testid="footer-terms-link">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className={`h-px mb-8 ${theme === 'dark' ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent' : 'bg-gradient-to-r from-transparent via-black/10 to-transparent'}`} />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[var(--muted)] text-sm">
            © {new Date().getFullYear()} Hotpeek Agency. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/login" 
              className="text-[var(--muted)] hover:text-[var(--primary)] transition-colors text-sm"
              data-testid="footer-admin-link"
            >
              Admin
            </Link>
          </div>
          {/* Instagram link hidden for now
          <div className="flex gap-6">
            <a href="https://www.instagram.com/wearehotpeek/" target="_blank" rel="noopener noreferrer" className="text-[var(--muted)] hover:text-[var(--primary)] transition-colors" data-testid="social-instagram">
              <Instagram size={20} />
            </a>
          </div>
          */}
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const { theme, toggleTheme } = useTheme();
  const [bannerHeight, setBannerHeight] = useState(0);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-[var(--background)] transition-colors duration-300">
        <div className="noise-overlay" />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: theme === 'dark' ? '#0A0A0A' : '#FFFFFF',
              border: theme === 'dark' ? '1px solid #222' : '1px solid #E0E0E0',
              color: theme === 'dark' ? '#fff' : '#0A0A0A'
            }
          }}
        />
        <Routes>
          <Route path="/" element={
            <>
              <TopBanner onHeightChange={setBannerHeight} />
              <Navbar scrollToSection={scrollToSection} theme={theme} toggleTheme={toggleTheme} topOffset={bannerHeight} />
              <main style={{ paddingTop: bannerHeight > 0 ? `${bannerHeight}px` : '0' }}>
                <HeroSection scrollToSection={scrollToSection} theme={theme} />
                <GrowthMarketingSection theme={theme} />
                <ServicesSection theme={theme} />
                <FreedomSection theme={theme} />
                <BenefitsSection theme={theme} />
                <PricingSection theme={theme} scrollToSection={scrollToSection} />
                <ReferralSection theme={theme} scrollToSection={scrollToSection} />
                <AdditionalRevenueSection theme={theme} />
                <MonetizationSection theme={theme} />
                <TestimonialsSection theme={theme} />
                <FAQSection theme={theme} />
                <ApplicationSection theme={theme} />
              </main>
              <Footer theme={theme} />
            </>
          } />
          <Route path="/terms" element={
            <>
              <Navbar scrollToSection={scrollToSection} theme={theme} toggleTheme={toggleTheme} />
              <TermsPage theme={theme} />
              <Footer theme={theme} />
            </>
          } />
          <Route path="/privacy" element={
            <>
              <Navbar scrollToSection={scrollToSection} theme={theme} toggleTheme={toggleTheme} />
              <PrivacyPage theme={theme} />
              <Footer theme={theme} />
            </>
          } />
          <Route path="/view/:viewKey" element={<ViewApplicationPage />} />
          
          {/* Creator Landing Page (public promotional link-tree style) */}
          <Route path="/c/:slug" element={<CreatorLandingPage />} />
          
          {/* Creator Public Profile (full profile with content) */}
          <Route path="/u/:username" element={<PublicCreatorProfilePage />} />
          
          {/* Auth Routes (root level) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Legacy redirects */}
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/app/login" element={<Navigate to="/login" replace />} />
          <Route path="/app/register" element={<Navigate to="/register" replace />} />
          <Route path="/app/verify-email" element={<Navigate to="/verify-email" replace />} />
          
          {/* Admin Routes (admin/manager only) */}
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<UserManagementPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
          <Route path="/admin/creator-requests" element={<AdminCreatorRequestsPage />} />
          <Route path="/admin/packages" element={<AdminPackagesPage />} />
          <Route path="/admin/transactions" element={<AdminTransactionsPage />} />
          <Route path="/admin/creators/:creatorId" element={<AdminCreatorProfilePage />} />
          <Route path="/admin/fans/:fanId" element={<AdminFanProfilePage />} />
          
          {/* User Routes (fan/creator only) */}
          <Route path="/app/profile" element={<PublicAuthProvider><ProfilePage /></PublicAuthProvider>} />
          <Route path="/app/payment-info" element={<PublicAuthProvider><PaymentInfoPage /></PublicAuthProvider>} />
        </Routes>
        <ImpersonationBanner />
      </div>
    </BrowserRouter>
  );
}

export default App;
